import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, PoundSterling, TrendingUp, Tags } from 'lucide-react';
import { SaveButton } from '../../components/ui/SaveButton';
import { reportError } from '../../lib/errors';
import toast from 'react-hot-toast';

interface PricingSettings {
    default_labor_rate: number;
    default_parts_markup: number;
}

export const PricingSettingsSection = () => {
    const [loading, setLoading] = useState(true);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [orgSettings, setOrgSettings] = useState<any>({});
    const [pricing, setPricing] = useState<PricingSettings>({
        default_labor_rate: 0,
        default_parts_markup: 0
    });

    useEffect(() => {
        const fetchOrg = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'pricing-settings-section.profiles');

            if (!profile?.active_org_id) return;
            setOrgId(profile.active_org_id);

            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('settings')
                .eq('id', profile.active_org_id)
                .single();
            if (fetchError) reportError(fetchError, 'pricing-settings-section.organizations');

            if (data?.settings) {
                setOrgSettings(data.settings);
                setPricing({
                    default_labor_rate: data.settings.default_labor_rate || 0,
                    default_parts_markup: data.settings.default_parts_markup || 0
                });
            }
            setLoading(false);
        };

        fetchOrg();
    }, []);

    const handleSave = async () => {
        if (!orgId) return;

        const updatedSettings = {
            ...orgSettings,
            default_labor_rate: pricing.default_labor_rate,
            default_parts_markup: pricing.default_parts_markup
        };

        const { error } = await supabase
            .from('organizations')
            .update({ settings: updatedSettings })
            .eq('id', orgId);

        if (error) {
            toast.error('Error updating pricing settings');
            throw error;
        } else {
            setOrgSettings(updatedSettings);
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <Tags className="w-5 h-5 text-accent" />
                    Pricing & Rates
                </h3>
                <p className="text-sm text-fg-2">Set your default labour rates and parts markup for quotes and jobs.</p>
            </div>

            <div className="space-y-6">
                {/* Default Labor Rate */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-fg-2 flex items-center gap-2">
                        <PoundSterling className="w-4 h-4" />
                        Global Hourly Labour Rate (£)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        value={pricing.default_labor_rate}
                        onChange={(e) => setPricing({ ...pricing, default_labor_rate: Number(e.target.value) || 0 })}
                        className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                        placeholder="e.g. 80"
                    />
                    <p className="text-xs text-fg-2">
                        This rate will automatically pre-populate when you add labour to a quote or job.
                    </p>
                    <p className="text-xs text-accent flex items-start gap-1.5">
                        <span aria-hidden>ℹ️</span>
                        <span>Enter this rate <strong>excluding VAT</strong>. If your business is VAT-registered, 20% VAT is added on top of this price on quotes and invoices.</span>
                    </p>
                </div>

                {/* Default Parts Markup */}
                <div className="space-y-4 pt-4 border-t border-line">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-fg-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Global Parts Markup (%)
                        </label>
                        <span className="text-lg font-bold text-accent">{pricing.default_parts_markup}%</span>
                    </div>
                    
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={pricing.default_parts_markup}
                        onChange={(e) => setPricing({ ...pricing, default_parts_markup: Number(e.target.value) || 0 })}
                        className="w-full h-2 bg-pressed rounded-lg appearance-none cursor-pointer accent-[var(--fds-accent)]"
                    />
                    
                    <div className="flex justify-between text-xs text-fg-2 px-1">
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                    </div>

                    <p className="text-xs text-fg-2 mt-2">
                        When adding parts, enter your cost and this markup will automatically calculate the customer's selling price.
                    </p>
                </div>

                <div className="pt-4">
                    <SaveButton onClick={handleSave} />
                </div>
            </div>
        </div>
    );
};
